import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile, useAuth } from '../../hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardBody,
  Avatar,
  Alert,
  FieldOfWorkField,
  PhotoCropModal,
} from '../../components';
import { DeleteAccountLink } from './DeleteAccount';

export const EditProfile: React.FC = () => {
  const { profile, loading, error, updateProfile, uploadPhoto, deletePhoto, clearError } = useProfile();
  const { user, accountType } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    field_of_work: '',
    phone: '',
    contact_email: '',
    rsa_id_number: '',
    passport_no: '',
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [cropSource, setCropSource] = useState<{ src: string; fileName: string } | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        bio: profile.bio || '',
        field_of_work: profile.field_of_work || '',
        phone: profile.phone || '',
        contact_email: profile.contact_email || user?.email || '',
        rsa_id_number: profile.rsa_id_number || '',
        passport_no: profile.passport_no || '',
      });
      setPhotoPreview(profile.photo_url ?? null);
    }
  }, [profile, user?.email]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = URL.createObjectURL(file);
    setCropSource({ src, fileName: file.name });
  };

  const handleCropCancel = () => {
    if (cropSource) URL.revokeObjectURL(cropSource.src);
    setCropSource(null);
    clearFileInput();
  };

  const handleCropComplete = (file: File, previewUrl: string) => {
    if (cropSource) URL.revokeObjectURL(cropSource.src);
    setCropSource(null);
    clearFileInput();
    setPhotoPreview(previewUrl);
    // Queue the upload — will fire after the profile row is saved
    setPendingPhoto(file);
  };

  const handleDeletePhoto = async () => {
    await deletePhoto();
    setPendingPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    const result = await updateProfile(formData);
    if (!result.success) return;

    if (pendingPhoto) {
      await uploadPhoto(pendingPhoto);
      setPendingPhoto(null);
    }

    setSuccess(true);
    setTimeout(() => navigate(accountType === 'verifier' ? '/verifier/profile' : '/profile'), 1500);
  };

  return (
    <div>
      {cropSource && (
        <PhotoCropModal
          isOpen
          imageSrc={cropSource.src}
          fileName={cropSource.fileName}
          title="Adjust profile photo"
          onCancel={handleCropCancel}
          onComplete={handleCropComplete}
        />
      )}
      <Card collapsible={false}>
        <CardHeader>
          <h1 className="text-2xl font-bold">Edit Profile</h1>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert variant="danger" onClose={clearError} className="mb-4">
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" className="mb-4">
              Profile updated successfully!
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col items-center mb-6">
              <Avatar
                src={photoPreview}
                alt="Profile"
                size="xl"
                fallback={formData.first_name?.charAt(0) || formData.last_name?.charAt(0)}
                className="mb-4"
              />
              
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                  className="sm:w-auto"
                >
                  Change Photo
                </Button>
                
                {photoPreview && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={handleDeletePhoto}
                    fullWidth
                    className="sm:w-auto"
                  >
                    Remove Photo
                  </Button>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                autoComplete="given-name"
              />
              
              <Input
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                autoComplete="family-name"
              />
            </div>
            
            <Input
              label="Phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
              inputMode="tel"
            />

            <Input
              label="Contact Email (visible to managers)"
              name="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleChange}
              autoComplete="email"
            />

            <FieldOfWorkField
              value={formData.field_of_work}
              onChange={field_of_work => setFormData(prev => ({ ...prev, field_of_work }))}
            />
            
            {/* Identity numbers */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-0.5">Identity Verification</p>
                <p className="text-xs text-gray-400">Optional. Add these if you want employers to verify your identity.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="RSA ID Number (optional)"
                  name="rsa_id_number"
                  value={formData.rsa_id_number}
                  onChange={handleChange}
                  maxLength={13}
                  placeholder="13-digit ID number"
                  inputMode="numeric"
                />
                <Input
                  label="Passport Number (optional)"
                  name="passport_no"
                  value={formData.passport_no}
                  onChange={handleChange}
                  maxLength={20}
                  placeholder="Passport number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bio
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[120px] text-base md:px-3 md:py-2 md:min-h-0"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" loading={loading} fullWidth className="sm:w-auto">
                Save Changes
              </Button>
              
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(accountType === 'verifier' ? '/verifier/profile' : '/profile')}
                fullWidth
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {accountType === 'verifier' ? <DeleteAccountLink /> : null}
    </div>
  );
};